import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SearchIcon, UserSearchIcon } from 'lucide-react';
import { Avatar, Button, EmptyState, LoadingState, SearchInput } from '@bcecloud/portal-ui';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { personName, searchUsers } from '@/lib/events';

/** The kinds of person a roster section can add. */
export const ROSTER_KIND = {
  team: 'team',
  facilitator: 'facilitator',
  client: 'client',
} as const;
export type RosterKind = (typeof ROSTER_KIND)[keyof typeof ROSTER_KIND];

/** Per-section wording and the account roles its search is limited to. */
export const ROSTER_KINDS: Record<
  RosterKind,
  {
    readonly action: string;
    readonly hint: string;
    /** Plural for the empty states: "No facilitators match…". */
    readonly plural: string;
    /** What to try when nobody matches. */
    readonly noMatch: string;
    readonly roles: readonly string[];
  }
> = {
  [ROSTER_KIND.team]: {
    action: 'Add BCE staff',
    hint: 'Search BCE employees by name or email.',
    plural: 'BCE staff',
    noMatch: 'Check the spelling, or search by email address.',
    roles: ['internal', 'admin', 'superadmin'],
  },
  [ROSTER_KIND.facilitator]: {
    action: 'Add facilitator',
    hint: 'Search contract facilitators by name or email.',
    plural: 'facilitators',
    noMatch: 'Check the spelling, or search by email address. A facilitator needs a bcecloud account before they can be added.',
    roles: ['contractor'],
  },
  [ROSTER_KIND.client]: {
    action: 'Add client contact',
    hint:
      'Search client accounts by name or email. Someone without an account gets access by being a contact on the Salesforce opportunity.',
    plural: 'client contacts',
    noMatch: 'Only clients who have signed in to My Events have an account. Add anyone new as a contact on the Salesforce opportunity.',
    roles: ['client'],
  },
};

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;
/** Fixed so the dialog never changes size between empty, loading and results. */
const RESULTS_HEIGHT = 'h-[340px]';

export interface AddEventPersonDialogProps {
  readonly kind: RosterKind;
  /** People already on the event; they show as "On event" instead of Add. */
  readonly assignedIds: ReadonlySet<string>;
  readonly adding: boolean;
  readonly onAdd: (userId: string) => void;
  readonly onClose: () => void;
}

/**
 * Searches one kind of bcecloud account (staff, facilitator or client) and
 * adds the chosen person to the event. The role filter is what makes each
 * section's Add button add only its own kind of person.
 *
 * @param props - The section kind, current roster and add handler.
 * @returns The dialog.
 */
export function AddEventPersonDialog({
  kind,
  assignedIds,
  adding,
  onAdd,
  onClose,
}: AddEventPersonDialogProps): React.ReactElement {
  const config = ROSTER_KINDS[kind];
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const query = debounced.trim();
  const candidates = useQuery({
    queryKey: ['user-search', kind, query],
    queryFn: ({ signal }) => searchUsers(query, signal, config.roles),
    enabled: query.length >= MIN_SEARCH_LENGTH,
  });

  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent className="sm:max-w-lg">
        <DialogTitle>{config.action}</DialogTitle>
        <DialogDescription className="text-portal-small text-portal-muted">
          {config.hint}
        </DialogDescription>
        <SearchInput
          label={`Search to ${config.action.toLowerCase()}`}
          placeholder="Start typing a name…"
          value={search}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
          autoFocus
        />
        <div className={`${RESULTS_HEIGHT} overflow-y-auto`}>
          {query.length < MIN_SEARCH_LENGTH ? (
            <EmptyState
              icon={<UserSearchIcon />}
              title={`Find ${config.plural}`}
              message="Type at least two letters of a name or email address."
            />
          ) : candidates.isPending ? (
            <LoadingState label="Searching…" />
          ) : (candidates.data ?? []).length === 0 ? (
            <EmptyState
              icon={<SearchIcon />}
              title={`No ${config.plural} match “${query}”`}
              message={config.noMatch}
            />
          ) : (
            <ul className="space-y-1">
              {(candidates.data ?? []).map((person) => (
                <li
                  key={person._id}
                  className="flex items-center gap-2.5 rounded-portal-button p-2 hover:bg-portal-bg"
                >
                  <Avatar name={personName(person)} />
                  <span className="min-w-0 flex-1">
                    <b className="block truncate text-portal-small">{personName(person)}</b>
                    <span className="block truncate text-portal-micro text-portal-muted">
                      {person.email}
                    </span>
                  </span>
                  {assignedIds.has(person._id) ? (
                    <span className="text-portal-micro text-portal-muted">On event</span>
                  ) : (
                    <Button
                      variant="secondary"
                      size="compact"
                      disabled={adding}
                      onClick={() => onAdd(person._id)}
                    >
                      Add
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
